import {
  FC,
  memo,
  useCallback,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { setHours, setMinutes } from 'date-fns';
import { Calendar, Clock } from 'lucide-react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import { theme } from 'theme';

import {
  Container,
  Label,
  InputContainer,
  StyledInput,
  IconContainer,
  ErrorText,
  DropdownContainer,
  TimeInputContainer,
  TimeInputWrapper,
  TimeInput,
  TimeIconButton,
  CustomTimeDropdown,
  TimeColumn,
  TimeColumnHeader,
  TimeOptionsList,
  TimeOption,
  DayPickerWrapper,
} from './styled';

type Props = {
  isDateTimePicker?: boolean;
  value?: string;
  onChange?: (date: string) => void;
  label?: ReactNode | string;
  errorText?: string;
  timeErrorText?: string;
  isSkewed?: boolean;
  placeholder?: string;
} & Omit<
  DayPickerProps,
  'selected' | 'onSelect' | 'mode' | 'startMonth' | 'endMonth' | 'disabled'
>;

const DatePicker: FC<Props> = ({
  isDateTimePicker = false,
  value,
  onChange,
  label,
  errorText,
  timeErrorText,
  isSkewed,
  placeholder = 'Select date',
  ...props
}) => {
  const [selected, setSelected] = useState<Date | undefined>(
    value ? new Date(value) : undefined,
  );
  const [timeValue, setTimeValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [tempHour, setTempHour] = useState<string>('');
  const [tempMinute, setTempMinute] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const timeDropdownRef = useRef<HTMLDivElement>(null);

  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, '0'),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, '0'),
  );

  const formatDisplayValue = useCallback(() => {
    if (!selected) {
      return '';
    }

    if (isDateTimePicker) {
      return selected.toLocaleString();
    }

    return selected.toLocaleDateString();
  }, [selected, isDateTimePicker]);

  const handleTimeChange = useCallback(
    (newTimeValue: string) => {
      setTimeValue(newTimeValue);

      if (!selected) return;

      const [hours, minutes] = newTimeValue
        .split(':')
        .map((str) => parseInt(str, 10));
      const newSelectedDate = setHours(setMinutes(selected, minutes), hours);
      setSelected(newSelectedDate);
      onChange?.(newSelectedDate.toISOString());
      setTempHour('');
      setTempMinute('');
    },
    [selected, onChange],
  );

  const handleInputClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleTimeIconClick = useCallback(() => {
    setIsTimeDropdownOpen((prev) => !prev);
  }, []);

  const handleHourSelect = useCallback(
    (hour: string) => {
      setTempHour(hour);
      if (tempMinute) {
        const newTime = `${hour}:${tempMinute}`;
        handleTimeChange(newTime);
      }
    },
    [tempMinute, handleTimeChange],
  );

  const handleMinuteSelect = useCallback(
    (minute: string) => {
      setTempMinute(minute);
      if (tempHour) {
        const newTime = `${tempHour}:${minute}`;
        handleTimeChange(newTime);
      }
    },
    [tempHour, handleTimeChange],
  );

  const handleDaySelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        setSelected(undefined);
        onChange?.('');
        return;
      }

      let newDate = date;

      if (isDateTimePicker && timeValue) {
        const [hours, minutes] = timeValue
          .split(':')
          .map((str) => parseInt(str, 10));
        newDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          hours,
          minutes,
        );
      }

      setSelected(newDate);
      onChange?.(newDate.toISOString());

      if (!isDateTimePicker) {
        setIsOpen(false);
      }
    },
    [onChange, isDateTimePicker, timeValue],
  );

  useEffect(() => {
    if (value && value !== selected?.toISOString()) {
      const newDate = new Date(value);
      setSelected(newDate);
      if (isDateTimePicker) {
        const hours = newDate.getHours().toString().padStart(2, '0');
        const minutes = newDate.getMinutes().toString().padStart(2, '0');
        setTimeValue(`${hours}:${minutes}`);
      }
    }
  }, [value, selected, isDateTimePicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTimeDropdownOpen(false);
      }
    };

    if (isOpen || isTimeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isTimeDropdownOpen]);

  return (
    <Container ref={containerRef}>
      {!label ? null : typeof label === 'string' ? (
        <Label>{label}</Label>
      ) : (
        label
      )}

      <InputContainer
        $isError={!!errorText}
        $isSkewed={isSkewed}
        $isOpen={isOpen}
        onClick={handleInputClick}
      >
        <StyledInput
          value={formatDisplayValue()}
          placeholder={placeholder}
          $isSkewed={isSkewed}
          readOnly
        />

        <IconContainer $isSkewed={isSkewed}>
          <Calendar size={16} color={theme.colors.grey[400]} />
        </IconContainer>
      </InputContainer>

      {!!errorText && <ErrorText>{errorText}</ErrorText>}

      <DropdownContainer $isOpen={isOpen}>
        {isDateTimePicker && (
          <TimeInputContainer>
            <Label>Set the time</Label>

            <TimeInputWrapper ref={timeDropdownRef}>
              <TimeInput
                type='text'
                value={timeValue}
                readOnly
                onClick={handleTimeIconClick}
                placeholder='Select time'
                $isError={!!timeErrorText}
              />
              <TimeIconButton onClick={handleTimeIconClick}>
                <Clock size={14} color={theme.colors.grey[300]} />
              </TimeIconButton>

              <CustomTimeDropdown $isOpen={isTimeDropdownOpen}>
                <TimeColumn>
                  <TimeColumnHeader>Hour</TimeColumnHeader>
                  <TimeOptionsList>
                    {hours.map((hour) => (
                      <TimeOption
                        key={hour}
                        $isSelected={
                          hour === (tempHour || timeValue.split(':')[0])
                        }
                        onClick={() => handleHourSelect(hour)}
                      >
                        {hour}
                      </TimeOption>
                    ))}
                  </TimeOptionsList>
                </TimeColumn>

                <TimeColumn>
                  <TimeColumnHeader>Min</TimeColumnHeader>
                  <TimeOptionsList>
                    {minutes.map((minute) => (
                      <TimeOption
                        key={minute}
                        $isSelected={
                          minute === (tempMinute || timeValue.split(':')[1])
                        }
                        onClick={() => handleMinuteSelect(minute)}
                      >
                        {minute}
                      </TimeOption>
                    ))}
                  </TimeOptionsList>
                </TimeColumn>
              </CustomTimeDropdown>
            </TimeInputWrapper>

            {!!timeErrorText && <ErrorText>{timeErrorText}</ErrorText>}
          </TimeInputContainer>
        )}

        <DayPickerWrapper>
          <DayPicker
            {...props}
            mode='single'
            selected={selected}
            onSelect={handleDaySelect}
            captionLayout='dropdown'
          />
        </DayPickerWrapper>
      </DropdownContainer>
    </Container>
  );
};

export default memo(DatePicker);
